// Central timezone formatter.
//
// Storage is UTC (ISO-8601 strings from the backend). Display timezone
// depends on the row:
//   * site-scoped rows  → the site's IANA name (e.g. `row.site_timezone`
//                          or `site.timezone`). Falls through to the
//                          agency default when missing.
//   * agency-wide rows  → the agency's configured timezone
//   * viewer-scoped rows→ omit `tz` entirely; the browser locale wins
//
// Every helper accepts a `tz` string (any IANA name). Passing `null`,
// `undefined`, or an empty string falls back to the viewer's browser
// timezone — which is what we want for auth events / audit logs / etc.

// Last-resort default used *only* when a helper is explicitly called with
// `APP_TZ` at the call site (e.g. for the ops team's home-office reports).
// Do NOT default to this globally — the whole point of this module is to
// respect per-site timezones.
export const APP_TZ = "America/Los_Angeles";

function _parse(input) {
  if (input == null || input === "") return null;
  const d = input instanceof Date ? input : new Date(input);
  return Number.isNaN(d.getTime()) ? null : d;
}

function _withTz(base, tz) {
  if (!tz) return base;
  return { ...base, timeZone: tz };
}

/**
 * Format a full date-time. When `tz` is a non-empty IANA name the value
 * is rendered in that timezone with a short zone label (e.g. "EDT").
 * Otherwise the browser locale/timezone applies.
 */
export function fmtDateTime(input, tz, opts = {}) {
  const d = _parse(input);
  if (!d) return "";
  const base = tz
    ? { hour: "numeric", minute: "2-digit", month: "short", day: "numeric", year: "numeric", timeZoneName: "short" }
    : {};
  return d.toLocaleString(opts.locale || undefined, _withTz({ ...base, ...opts }, tz));
}

/** Just the date portion in the requested tz (or browser default). */
export function fmtDate(input, tz, opts = {}) {
  const d = _parse(input);
  if (!d) return "";
  return d.toLocaleDateString(opts.locale || undefined, _withTz(opts, tz));
}

/** Just the time portion in the requested tz (or browser default). */
export function fmtTime(input, tz, opts = {}) {
  const d = _parse(input);
  if (!d) return "";
  const base = tz ? { hour: "numeric", minute: "2-digit", timeZoneName: "short" } : {};
  return d.toLocaleTimeString(opts.locale || undefined, _withTz({ ...base, ...opts }, tz));
}

/** Short date, e.g. "Feb 14, 2026". */
export function fmtDateShort(input, tz) {
  return fmtDate(input, tz, { year: "numeric", month: "short", day: "numeric" });
}

/** Short time, e.g. "3:45 PM EDT" when `tz` is set. */
export function fmtTimeShort(input, tz) {
  return fmtTime(input, tz, { hour: "numeric", minute: "2-digit", ...(tz ? { timeZoneName: "short" } : {}) });
}

/** Combined short label, e.g. "Feb 14, 3:45 PM EDT". */
export function fmtDateTimeShort(input, tz) {
  const d = _parse(input);
  if (!d) return "";
  const base = { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" };
  return d.toLocaleString(undefined, _withTz(tz ? { ...base, timeZoneName: "short" } : base, tz));
}

/**
 * Return the "YYYY-MM-DD" bucket for a timestamp as observed in a given
 * timezone. When `tz` is falsy, uses the viewer's local calendar day.
 */
export function dateKey(input, tz) {
  const d = _parse(input);
  if (!d) return "";
  return d.toLocaleDateString("en-CA", _withTz({}, tz));
}

// Pull the resolved site timezone from a row shape. Backend list
// endpoints attach `site_timezone` to every row that references a site;
// this shim lets callers stay terse.
export function siteTz(row) {
  return (row && (row.site_timezone || row.tz || row.timezone)) || null;
}
